/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getYear } from '../../../../utils';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../../../ews-template-labels';
import Loading from '../../../../../../components/Loading';
import useStore from '../../../../../../store';
import { toFormatCurrency } from '../../../../../../utils/format';

const Q_REPORT = gql`
	query getEarlyWarningGroupingRiskBasedReport($year: Float!, $companyType: InsuranceInstitutionsType!, $companyId: String) {
		getEarlyWarningGroupingRiskBasedReport(year: $year, companyType: $companyType, companyId: $companyId)
	}
`;

export default function RatioLifeGroupingRiskBasedSpreadSheetTable({
	year = null,
	companyType = null,
	companyId = null,
	groupingRiskData = null,
	requiredSolvencyData = null,
	lifeData = null,
	groupingRiskBasedExport = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState([]);
	const [q1Cells, setQ1Cells] = useState([]);
	const [q2Cells, setQ2Cells] = useState([]);
	const [q3Cells, setQ3Cells] = useState([]);
	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);

	const groupingRiskBased = [
		{ cells: q1Cells, setCells: setQ1Cells, index: 0 },
		{ cells: q2Cells, setCells: setQ2Cells, index: 1 },
		{ cells: q3Cells, setCells: setQ3Cells, index: 2 },
	];

	useEffect(load, [year, companyType, companyId]);

	useEffect(() => data?.getEarlyWarningGroupingRiskBasedReport && setDetail(data?.getEarlyWarningGroupingRiskBasedReport), [data]);

	useEffect(() => {
		if (detail && groupingRiskData && requiredSolvencyData && lifeData) {
			quarterDetail(detail);
		}
	}, [detail, groupingRiskData, requiredSolvencyData, lifeData]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { year: getYear(year), companyType, companyId };
		loadReport({ variables });
	}

	function emptyCells(
		labels = [],
		i,
		groupingRiskData = { q1Cells: [], q2Cells: [], q3Cells: [] },
		requiredSolvencyData = { q1Cells: [] },
		lifeData = { q4Cells: [] },
	) {
		const getCells = (labelCode) => {
			switch (labelCode) {
				case 'GRBRB':
					return [
						Array.isArray(groupingRiskData?.q3Cells[0]) ? groupingRiskData?.q3Cells[0] : [],
						Array.isArray(groupingRiskData?.q3Cells[1]) ? groupingRiskData?.q3Cells[1] : [],
						Array.isArray(groupingRiskData?.q3Cells[2]) ? groupingRiskData?.q3Cells[2] : [],
						Array.isArray(groupingRiskData?.q3Cells[3]) ? groupingRiskData?.q3Cells[3] : [],
					];
				case 'GRBSC':
					return [
						Array.isArray(requiredSolvencyData?.q1Cells[0]) ? requiredSolvencyData?.q1Cells[0] : [],
						Array.isArray(lifeData?.q4Cells?.[14]) ? lifeData?.q4Cells[14] : [],
					];
				case 'GRBCC':
					return [Array.isArray(lifeData?.q4Cells[15]) ? lifeData?.q4Cells[15] : []];
				default:
					return [];
			}
		};

		const calculateScore = (labelCode, cells, i) => {
			switch (labelCode) {
				case 'GRBRB':
					const [c1, c2, c3, c4] = cells;
					const avg = (Number(c1[0]?.value || 0) + Number(c2[0]?.value || 0) + Number(c3[0]?.value || 0) + Number(c4[0]?.value || 0)) / 4;
					switch (i) {
						case 0:
							return toFormatCurrency(avg, null);
						case 1:
							return 1;
						case 2:
							return toFormatCurrency(avg, null) * 1;
						default:
							return 0;
					}
				case 'GRBSC':
					const [sc1, sc2] = cells;
					const scCell = Number(sc1[0]?.value || 0);
					const sc1Cell = Number(sc2[0]?.value || 0);
					const riskSC = scCell >= 3500000 ? 0 : scCell >= 3000000 ? 1 : scCell >= 2500000 ? 2 : scCell >= 2000000 ? 3 : 4;
					switch (i) {
						case 0:
							return riskSC + sc1Cell;
						case 1:
							return 3;
						case 2:
							return (riskSC + sc1Cell) * 3;
						default:
							return 0;
					}
				case 'GRBCC':
					const [cc1] = cells;
					const ccCell = Number(cc1[0]?.value || 0);
					switch (i) {
						case 0:
							return toFormatCurrency(ccCell, null);
						case 1:
							return 2;
						case 2:
							return toFormatCurrency(ccCell, null) * 2;
						default:
							return 0;
					}
				default:
					return 0;
			}
		};

		return labels?.map((label) => {
			const cells = getCells(label?.code);
			const score = calculateScore(label?.code, cells, i);
			const truncated = Math.trunc(score * 100) / 100;

			return [
				{
					value: toFormatCurrency(truncated, null),
					readOnly: true,
				},
			];
		});
	}

	function quarterDetail(detailData) {
		if (!detailData || !detailData?.labels) return;

		// Risk Based
		const riskBased =
			detailData?.q1 && detailData?.q1?.length
				? quarterCells({ labels: detailData?.labels, quarter: detailData?.q1 })
				: emptyCells(detailData?.labels, 0, groupingRiskData, requiredSolvencyData, lifeData);

		// Solvency Compliance
		const solvencyCompliance =
			detailData?.q2 && detailData?.q2?.length
				? quarterCells({ labels: detailData?.labels, quarter: detailData?.q2 })
				: emptyCells(detailData?.labels, 1, groupingRiskData, requiredSolvencyData, lifeData);

		// Capital Compliance
		const capitalCompliance =
			detailData?.q3 && detailData?.q3?.length
				? quarterCells({ labels: detailData?.labels, quarter: detailData?.q3 })
				: emptyCells(detailData?.labels, 2, groupingRiskData, requiredSolvencyData, lifeData);

		const numRows = detailData?.labels?.length;
		const normalize = (cells) => (cells?.length === numRows ? cells : [...cells, ...Array(numRows - cells?.length).fill([{ value: '', readOnly: true }])]);

		setQ1Cells(normalize(riskBased));
		setQ2Cells(normalize(solvencyCompliance));
		setQ3Cells(normalize(capitalCompliance));

		groupingRiskBasedExport({
			labels: detailData?.labels,
			q1Cells: riskBased,
			q2Cells: solvencyCompliance,
			q3Cells: capitalCompliance,
		});
	}

	function quarterCells({ labels = [], quarter = null }) {
		const localCells = labels?.map((label) => {
			const fallbackValues = [{ value: 0, readOnly: true }];
			const found = quarter?.find((e) => e?.code === label?.code);
			const rowValues = found?.values || fallbackValues;

			return rowValues?.map((cell) => {
				let val = cell?.value;
				if (typeof val === 'number') val = truncateDecimal(val, 3);

				return {
					value: val,
					readOnly: true,
				};
			});
		});

		return localCells;
	}

	function truncateDecimal(value, digits = 3) {
		if (typeof value !== 'number') return value;
		const factor = 10 ** digits;
		return Math.trunc(value * factor) / factor;
	}

	function handleCellsChange(index, newData) {
		switch (index) {
			case 0:
				setQ1Cells(newData);
				break;
			case 1:
				setQ2Cells(newData);
				break;
			case 2:
				setQ3Cells(newData);
				break;
			default:
				break;
		}
	}

	const preventReadOnlyEdit = (oldData, newData) => {
		return newData.map((row, r) =>
			row.map((cell, c) => {
				if (oldData?.[r]?.[c]?.readOnly) {
					return oldData[r][c];
				}
				return cell;
			}),
		);
	};

	return (
		<>
			<Box>
				{loading && (
					<>
						<Loading />
					</>
				)}
				{!loading && !detail && (
					<>
						<Text color="gray.400" textAlign="center" mt={4}>
							{t('No Data')}
						</Text>
					</>
				)}
				{!loading && detail && (
					<>
						<Box className="ews-container">
							<EWSLabels type="EWS" labels={detail?.labels} active={active} />
							<Flex>
								{groupingRiskBased?.map(({ cells, setCells, index }) =>
									cells?.length > 0 ? (
										<Spreadsheet
											key={index}
											data={cells}
											onChange={(newData) => {
												const sanitized = preventReadOnlyEdit(cells, newData);

												handleCellsChange(index, sanitized);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 0, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									) : null,
								)}
							</Flex>
						</Box>
					</>
				)}
			</Box>
		</>
	);
}
